import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import { jsx, Global } from '@emotion/react';
import packageInfo from '@emotion/react/package.json';
import { __ThemeUIInternalBaseThemeProvider } from '@theme-ui/core';
import React, { useMemo } from 'react';
import ColorModeProvider from './color-mode-provider.esm.js';
import { css } from './css.esm.js';
import CSSReset from './css-reset.esm.js';
import { merge } from './merge-theme.esm.js';
import useThemeUI from './use-theme-ui.esm.js';
import { BoxPrivate } from '../components/_primitives/box/box.esm.js';
import { FLAT } from '../themes/const.esm.js';
import CostarSuiteTheme from '../themes/presets/costar-suite-theme.esm.js';
import { isBrowser } from '../utils/dom.esm.js';
import { getIdGenerator, IdProvider } from '../utils/unique-id.esm.js';
import { isEmptyObject, isNotEmptyObject } from '../utils/assertion.esm.js';
import { EMPTY_OBJECT } from '../utils/object.esm.js';

var _excluded = ["sx"];
var __EMOTION_VERSION__ = packageInfo.version;
var cacheThemes = {};
function resolveTheme(outer, theme) {
  var baseUnit = theme.baseUnit || outer.theme.baseUnit;
  var direction = theme.direction || outer.theme.direction || 'ltr';
  var cacheKey = theme.contextPath ? "".concat(theme.contextPath, ".").concat(baseUnit, ".").concat(direction) : undefined;
  var retVal = cacheKey ? cacheThemes[cacheKey] : undefined;
  if (!retVal) {
    retVal = merge.all({}, outer, {
      theme: theme
    });
    if (cacheKey) {
      cacheThemes[cacheKey] = retVal;
    }
  }
  return retVal;
}
function ThemeProvider(_ref) {
  var theme = _ref.theme,
    children = _ref.children;
  var outer = useThemeUI();
  if (process.env.NODE_ENV !== 'production') {
    if (outer.__EMOTION_VERSION__ !== __EMOTION_VERSION__) {
      // eslint-disable-next-line no-console
      console.warn('Multiple versions of Emotion detected,', 'and theming might not work as expected.', 'Please ensure there is only one copy of @emotion/react installed in your application.');
    }
  }
  var context = typeof theme === 'function' && outer.theme ? _objectSpread2(_objectSpread2({}, outer), {}, {
    theme: theme(outer.theme)
  }) :
  //@ts-ignore
  resolveTheme(outer, theme);

  // @ts-ignore
  return jsx(__ThemeUIInternalBaseThemeProvider, {
    context: context
  }, children);
}
var RootStyles = function RootStyles() {
  return jsx(Global, {
    styles: function styles(emotionTheme) {
      var theme = emotionTheme;
      var _ref2 = theme.config || theme,
        useRootStyles = _ref2.useRootStyles;
      if (useRootStyles === false || theme.styles && !theme.styles.root) {
        return null;
      }
      var boxSizing = theme.useBorderBox === false ? undefined : 'border-box';
      return css({
        '*': {
          boxSizing: boxSizing
        },
        html: {
          variant: 'styles.root'
        },
        body: {
          margin: 0
        }
      })(theme);
    }
  });
};
var ThemeUIProvider = function ThemeUIProvider(props) {
  var _props$theme = props.theme,
    theme = _props$theme === void 0 ? CostarSuiteTheme : _props$theme,
    hoistContext = props.hoistContext,
    resetCSS = props.resetCSS,
    _props$disableSSR = props.disableSSR,
    disableSSR = _props$disableSSR === void 0 ? false : _props$disableSSR,
    children = props.children,
    _useDivRootContext = props.useDivRootContext,
    _props$enableFlatDesi = props.enableFlatDesign,
    enableFlatDesign = _props$enableFlatDesi === void 0 ? false : _props$enableFlatDesi,
    _props$includeGlobalR = props.includeGlobalRootStyle,
    includeGlobalRootStyle = _props$includeGlobalR === void 0 ? false : _props$includeGlobalR,
    _props$divRootProps = props.divRootProps,
    divRootProps = _props$divRootProps === void 0 ? EMPTY_OBJECT : _props$divRootProps,
    colorModeEnable = props.colorModeEnable,
    idProviderEnable = props.idProviderEnable;
  var _useThemeUI = useThemeUI(),
    _useThemeUI$theme = _useThemeUI.theme,
    outerTheme = _useThemeUI$theme === void 0 ? {} : _useThemeUI$theme;
  var outerThemeContextPath = outerTheme.contextPath || '';
  var sx = divRootProps.sx,
    restDivRootProps = _objectWithoutProperties(divRootProps, _excluded);
  var useDivRootContext = _useDivRootContext || !isEmptyObject(divRootProps);
  // eslint-disable-next-line no-console
  // console.log(`--- ThemeUIProvider initializing  ${theme.contextId} ----`)

  var __css = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, sx), (theme === null || theme === void 0 ? void 0 : theme.direction) === 'rtl' ? {
      direction: 'rtl'
    } : {});
  }, [theme === null || theme === void 0 ? void 0 : theme.direction, sx]);

  // context has already been establish no need to initialized another context,
  // however if hoisting is require initialize the root style with a div wrapper.
  // first scenario handles the base theme context, second scenario handles existing alternate context
  if (isNotEmptyObject(theme) && theme.contextId && (outerTheme.contextId === theme.contextId || outerThemeContextPath.includes(theme.contextId))) {
    var _theme$contextPath;
    return hoistContext ? /*#__PURE__*/React.createElement(BoxPrivate, _extends({
      className: "csg-tui-context-root-".concat((_theme$contextPath = theme.contextPath) === null || _theme$contextPath === void 0 ? void 0 : _theme$contextPath.replaceAll('.', '-')),
      variant: "styles.root",
      __css: __css
    }, restDivRootProps), children) : /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
  if (isNotEmptyObject(theme)) {
    if (!theme.contextId) {
      theme.contextId = "".concat(getIdGenerator('theme')());
    }
    theme.contextPath = outerThemeContextPath ? "".concat(outerThemeContextPath, ".").concat(theme.contextId) : theme.contextId;
    if (enableFlatDesign && !theme.contextPath.includes(FLAT)) {
      theme.contextPath = "".concat(theme.contextPath, ".").concat(FLAT);
    }
    // eslint-disable-next-line no-console
    // console.log(
    //   `--- ThemeUIProvider set contextId ${theme.contextId} contextPath: ${theme.contextPath}----`
    // )
    theme.outerTheme = outerTheme;
  }
  function wrapIdProvider(children) {
    return idProviderEnable ? /*#__PURE__*/React.createElement(IdProvider, null, children) : /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
  function wrapColorModeProvider(children) {
    return colorModeEnable ? /*#__PURE__*/React.createElement(ColorModeProvider, null, children) : /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
  function wrapDivRootContext(children) {
    var _theme$contextPath2;
    return useDivRootContext ? /*#__PURE__*/React.createElement(BoxPrivate, _extends({
      className: "csg-tui-context-root-".concat((_theme$contextPath2 = theme.contextPath) === null || _theme$contextPath2 === void 0 ? void 0 : _theme$contextPath2.replaceAll('.', '-')),
      variant: "styles.root",
      __css: __css
    }, restDivRootProps), children) : /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
  return isEmptyObject(theme) ? /*#__PURE__*/React.createElement(React.Fragment, null, children) : disableSSR && !isBrowser ? null : /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, resetCSS === true ? CSSReset() : resetCSS && CSSReset(resetCSS), includeGlobalRootStyle ? /*#__PURE__*/React.createElement(RootStyles, null) : null, wrapDivRootContext(wrapColorModeProvider(wrapIdProvider(children))));
};

export default ThemeUIProvider;
//# sourceMappingURL=theme-ui-provider.esm.js.map
